import {useContext} from 'react'

import Context from '@s-ui/react-context'
import CalendarOutline from '@adv-mt/mt-svg-icons/lib/CalendarOutline'
import CarExtraseguridadOutline from '@adv-mt/mt-svg-icons/lib/CarExtraseguridadOutline'
import CarLengthOutline from '@adv-mt/mt-svg-icons/lib/CarLengthOutline'
import CarOutline from '@adv-mt/mt-svg-icons/lib/CarOutline'
import CarTallOutline from '@adv-mt/mt-svg-icons/lib/CarTallOutline'
import CarWidthBasicIcon from '@adv-mt/mt-svg-icons/lib/CarWidthBasic'
import CilinderOutline from '@adv-mt/mt-svg-icons/lib/CilinderOutline'
import CilynderOutlineIcon from '@adv-mt/mt-svg-icons/lib/CilynderOutline'
import CngIcon from '@adv-mt/mt-svg-icons/lib/Cng'
import DoorsOutline from '@adv-mt/mt-svg-icons/lib/DoorsOutline'
import FuelDieselOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelDieselOutline'
import FuelElectricOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelElectricOutline'
import FuelGasolineOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelGasolineOutline'
import FuelHybridOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelHybridOutline'
import FuelHybridPlugInOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelHybridPlugInOutline'
import FuelOutlineIcon from '@adv-mt/mt-svg-icons/lib/FuelOutline'
import GearAutoIcon from '@adv-mt/mt-svg-icons/lib/GearAuto'
import GearManualIcon from '@adv-mt/mt-svg-icons/lib/GearManual'
import KmOdometer from '@adv-mt/mt-svg-icons/lib/KmOdometer'
import Leaf from '@adv-mt/mt-svg-icons/lib/Leaf'
import LpgIcon from '@adv-mt/mt-svg-icons/lib/Lpg'
import MoreVertIcon from '@adv-mt/mt-svg-icons/lib/MoreVert'
import PaletteOutline from '@adv-mt/mt-svg-icons/lib/PaletteOutline'
import PoiOutline from '@adv-mt/mt-svg-icons/lib/PoiOutline'

import {BASE_CLASS} from './config.js'

const MANUAL_TRANSMISSION = 'manual'

const getEnvLabelIcon = (envLabelId, ENVIRONMENTAL_LABEL_ICONS, urlStatics) => {
  const iconPath = ENVIRONMENTAL_LABEL_ICONS?.[envLabelId]
  if (!iconPath) return Leaf
  return <img alt="" className={`${BASE_CLASS}-envLabelIcon`} src={`${urlStatics}${iconPath}`} />
}

const getColorIcon = (color, isMotos) => {
  if (isMotos || !color || color.includes(',')) return PaletteOutline
  const rawColor = color.split('.').pop() ?? ''
  const colorName = rawColor.replace(/^color_/i, '').toLowerCase()
  const colorType = `color${colorName[0]?.toUpperCase() ?? ''}${colorName.slice(1)}`
  return <span aria-hidden="true" className={`${BASE_CLASS}-colorIcon ${BASE_CLASS}-colorIcon--${colorType}`} />
}

const useCharacteristics = ({ad = {}, vehicleInfo = {}}) => {
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')
  const isMotos = config.get('APP_NAME') !== config.get('APP_NAME_COCHES')
  const urlStatics = config.get('urlStatics')
  const {ENVIRONMENTAL_LABEL_ICONS, fuelTypes, specificFueltypes, transmissionTypes, environmentalLabels} =
    config.get('search')
  const {FUEL_TYPES_IDS} = config.get('vehicleSpecifications')

  const FUEL_ICON_BY_FUEL_TYPE_ID = {
    [FUEL_TYPES_IDS.DIESEL]: FuelDieselOutlineIcon,
    [FUEL_TYPES_IDS.GASOLINE]: FuelGasolineOutlineIcon,
    [FUEL_TYPES_IDS.ELECTRIC]: FuelElectricOutlineIcon,
    [FUEL_TYPES_IDS.HYBRID]: FuelHybridOutlineIcon,
    [FUEL_TYPES_IDS.HYBRID_PLUG_IN]: FuelHybridPlugInOutlineIcon,
    [FUEL_TYPES_IDS.GLP]: LpgIcon,
    [FUEL_TYPES_IDS.CNG]: CngIcon,
    [FUEL_TYPES_IDS.OTHER]: MoreVertIcon
  }

  const {specifications = {}} = vehicleInfo
  const {bodyType, cubicCapacity, doors, hp, height, length, powerElectric, seats, width} = specifications

  const availableFuelTypes = specificFueltypes?.length ? specificFueltypes : fuelTypes
  const fuelTypeEntry = availableFuelTypes?.find(ft => ft.id === ad.fuelTypeId)
  const fuelTypeLabel = fuelTypeEntry ? i18n.t(fuelTypeEntry.label) : null

  const transmissionEntry = transmissionTypes?.find(t => Number(t.id) === Number(ad.transmissionTypeId))
  const transmissionLabel = transmissionEntry ? i18n.t(transmissionEntry.label) : null
  const isAutomatic = transmissionLabel && !transmissionLabel.toLowerCase().includes(MANUAL_TRANSMISSION)
  const TransmissionIcon = isAutomatic ? GearAutoIcon : GearManualIcon

  const environmentalLabel = environmentalLabels?.find(el => el.id === ad.environmentalLabel)
  const environmentalLabelText = environmentalLabel ? i18n.t(environmentalLabel.label) : null

  const items = [
    ad.km && {
      icon: KmOdometer,
      label: i18n.t('COMPARATOR.TABLE.ROW_KM'),
      value: `${i18n.n(ad.km)} ${i18n.t('COMMON.UNIT_KMS')}`
    },
    ad.year && {
      icon: CalendarOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_YEAR'),
      value: String(ad.year)
    },
    fuelTypeLabel && {
      icon: FUEL_ICON_BY_FUEL_TYPE_ID[ad.fuelTypeId] ?? FuelOutlineIcon,
      label: i18n.t('COMPARATOR.TABLE.ROW_FUEL'),
      value: fuelTypeLabel
    },
    environmentalLabelText && {
      icon: getEnvLabelIcon(ad.environmentalLabel, ENVIRONMENTAL_LABEL_ICONS, urlStatics),
      label: i18n.t('COMPARATOR.TABLE.ROW_ENV_LABEL'),
      value: environmentalLabelText
    },
    ad.province && {
      icon: PoiOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_PROVINCE'),
      value: ad.province
    },
    transmissionLabel && {
      icon: TransmissionIcon,
      label: i18n.t('COMPARATOR.TABLE.ROW_TRANSMISSION'),
      value: transmissionLabel
    },
    hp && {
      icon: CilynderOutlineIcon,
      label: i18n.t('COMPARATOR.TABLE.ROW_HP'),
      value: `${hp} ${i18n.t('COMMON.UNIT_HP')}`
    },
    powerElectric && {
      icon: CilynderOutlineIcon,
      label: i18n.t('COMPARATOR.TABLE.ROW_HP'),
      value: `${powerElectric} ${i18n.t('COMMON.UNIT_KW')}`
    },
    cubicCapacity && {
      icon: CilinderOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_CUBIC_CAPACITY'),
      value: `${cubicCapacity} ${i18n.t('COMMON.UNIT_CC')}`
    },
    bodyType && {
      icon: CarOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_BODY_TYPE'),
      value: i18n.t(bodyType)
    },
    doors && {
      icon: DoorsOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_DOORS'),
      value: String(doors)
    },
    seats && {
      icon: CarExtraseguridadOutline,
      label: i18n.t('COMPARATOR.TABLE.ROW_SEATS'),
      value: String(seats)
    },
    ad.color && {
      icon: getColorIcon(ad.color, isMotos),
      label: i18n.t('COMPARATOR.TABLE.ROW_COLOR'),
      value: i18n.t(ad.color)
    },
    length && {
      icon: CarLengthOutline,
      label: i18n.t('MODEL_SIZES.VEHILCE_SIZES_SECTION.LENGTH'),
      value: `${length} ${i18n.t('COMMON.UNIT_MM')}`
    },
    width && {
      icon: CarWidthBasicIcon,
      label: i18n.t('MODEL_SIZES.VEHILCE_SIZES_SECTION.WIDTH'),
      value: `${width} ${i18n.t('COMMON.UNIT_MM')}`
    },
    height && {
      icon: CarTallOutline,
      label: i18n.t('MODEL_SIZES.VEHILCE_SIZES_SECTION.HEIGHT'),
      value: `${height} ${i18n.t('COMMON.UNIT_MM')}`
    }
  ].filter(Boolean)

  return {items}
}

export default useCharacteristics
